/*! JointJS+ v4.0.1 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-04-11 


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import { dia, mvc, shapes } from '@joint/plus';

import {
    ENTITY_ICON,
    FLOW_ICON,
    FONT_FAMILY,
    LIGHT_COLOR,
    LLM_ICON,
    MAIN_COLOR,
    MESSAGE_ICON,
    PADDING_L,
    RESPONSE_PARAM_ICON,
    TALK_ATTENDANT_ICON,
} from '../../theme';

export enum ShapeTypesEnum {
    MESSAGE = 'stencil.Message',
    FLOWCHART_START = 'stencil.FlowchartStart',
    FLOWCHART_END = 'stencil.FlowchartEnd',
    RESPONSE_ORIENTATION = 'stencil.ResponseOrientation',
    TALK_ATTENDANT = 'stencil.TalkAttendant',
    RESPONSE_PARAM = 'stencil.ResponseParam',
    SERVICE = 'stencil.Service',
    FLOW = 'stencil.Flow',
    CREATE_TICKET = 'stencil.CreateTicket',
    AVALIATION = 'stencil.Avaliation',
    LLM = 'stencil.LLM',
    CONDITION = 'stencil.Condition'
}

const SHAPE_SIZE = 48;

const FlowchartStart = dia.Element.define(ShapeTypesEnum.FLOWCHART_START, {
    name: 'FlowchartStart',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: MAIN_COLOR,
            stroke: 'none',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            d: 'M 2 8 L 4.29 5.71 L 1.41 2.83 L 2.83 1.41 L 5.71 4.29 L 8 2 L 8 8 Z M -2 8 L -8 8 L -8 2 L -5.71 4.29 L -1 -0.41 L -1 -8 L 1 -8 L 1 0.41 L -4.29 5.71 Z',
            fill: '#FFFFFF',
            transform: 'translate(calc(0.5 * w), calc(0.5 * h))'
        },
        label: {
            text: 'Start',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'path',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }],
});

const FlowchartEnd = dia.Element.define(ShapeTypesEnum.FLOWCHART_END, {
    name: 'FlowchartEnd',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: MAIN_COLOR,
            stroke: 'none',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)'
        },
        icon: {
            d: 'M 5 -8.45 L 6.41 -7.04 L 3 -3.635 L 1.59 -5.04 Z M -4.5 3.95 L -1 3.95 L -1 -1.63 L -6.41 -7.04 L -5 -8.45 L 1 -2.45 L 1 3.95 L 4.5 3.95 L 0 8.45 Z',
            fill: '#FFFFFF',
            transform: 'translate(calc(0.5 * w), calc(0.5 * h))'
        },
        label: {
            text: 'End',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        },
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'path',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }],
});

const Message = dia.Element.define(ShapeTypesEnum.MESSAGE, {
    name: 'Message',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: MESSAGE_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const ResponseOrientation = dia.Element.define(ShapeTypesEnum.RESPONSE_ORIENTATION, {
    name: 'ResponseOrientation',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: MESSAGE_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const TalkAttendant = dia.Element.define(ShapeTypesEnum.TALK_ATTENDANT, {
    name: 'TalkAttendant',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: TALK_ATTENDANT_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const ResponseParam = dia.Element.define(ShapeTypesEnum.RESPONSE_PARAM, {
    name: 'ResponseParam',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: RESPONSE_PARAM_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const Service = dia.Element.define(ShapeTypesEnum.SERVICE, {
    name: 'Service',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: ENTITY_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const CreateTicket = dia.Element.define(ShapeTypesEnum.CREATE_TICKET, {
    name: 'CreateTicket',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: ENTITY_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const Avaliation = dia.Element.define(ShapeTypesEnum.AVALIATION, {
    name: 'Avaliation',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: ENTITY_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const Flow = dia.Element.define(ShapeTypesEnum.FLOW, {
    name: 'Flow',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: FLOW_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const LLM = dia.Element.define(ShapeTypesEnum.LLM, {
    name: 'LLM',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: LLM_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

const Condition = dia.Element.define(ShapeTypesEnum.CONDITION, {
    name: 'Condition',
    size: { width: SHAPE_SIZE, height: SHAPE_SIZE },
    attrs: {
        body: {
            fill: LIGHT_COLOR,
            stroke: '#E8E8E8',
            cx: 'calc(0.5 * w)',
            cy: 'calc(0.5 * h)',
            r: 'calc(0.5 * w)',
        },
        icon: {
            width: 20,
            height: 20,
            x: 'calc(0.5 * w - 10)',
            y: 'calc(0.5 * h - 10)',
            xlinkHref: LLM_ICON
        },
        label: {
            text: 'Component',
            x: `calc(w + ${PADDING_L})`,
            y: 'calc(0.5 * h)',
            textAnchor: 'start',
            textVerticalAnchor: 'middle',
            fill: '#242424',
            fontFamily: FONT_FAMILY,
            fontSize: 13
        }
    }
} as mvc.ObjectHash, {
    markup: [{
        tagName: 'circle',
        selector: 'body'
    }, {
        tagName: 'image',
        selector: 'icon'
    }, {
        tagName: 'text',
        selector: 'label'
    }]
});

Object.assign(shapes, {
    stencil: {
        Message,
        FlowchartStart,
        FlowchartEnd,
        ResponseOrientation,
        TalkAttendant,
        ResponseParam,
        Service,
        Flow,
        CreateTicket,
        Avaliation,
        LLM,
        Condition
    }
});
